import { DeviceCountBranchRow, DeviceCountDevice, DeviceCountFlatRow } from './types';

export const getMonthValue = (date: Date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

export const getMonthLabel = (value: string, locale?: string) => {
    const [year, month] = value.split('-').map(Number);
    if (!year || !month) return '';

    return new Date(year, month - 1).toLocaleDateString(locale, {
        month: 'long',
        year: 'numeric',
    });
};

const getDateOnly = (value: string) => {
    const [year, month, day] = value.split('T')[0].split('-').map(Number);
    if (!year || !month || !day) return null;

    return new Date(year, month - 1, day);
};

export const getDaysUntilDate = (value?: string | null) => {
    if (!value) return null;

    const targetDate = getDateOnly(value);
    if (!targetDate) return null;

    const today = new Date();
    const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const dayInMs = 24 * 60 * 60 * 1000;

    return Math.round((targetDate.getTime() - todayDate.getTime()) / dayInMs);
};

export const isToday = (value?: string | null) => getDaysUntilDate(value) === 0;

const isFlatDeviceRow = (row: DeviceCountBranchRow | DeviceCountFlatRow): row is DeviceCountFlatRow =>
    'branchId' in row || 'deviceId' in row;

export const normalizeDeviceRows = (rows: Array<DeviceCountBranchRow | DeviceCountFlatRow>): DeviceCountBranchRow[] => {
    const groupedByBranch = new Map<number, DeviceCountBranchRow>();
    const deviceKeysByBranch = new Map<number, Set<string>>();

    const addDeviceToBranch = (branchId: number, branch: DeviceCountBranchRow, device: DeviceCountDevice) => {
        const deviceKeys = deviceKeysByBranch.get(branchId) ?? new Set<string>();
        const deviceKey = `${device.historyId ?? 'history'}-${device.id}-${device.changedAt ?? ''}-${
            device.operation ?? ''
        }`;

        if (deviceKeys.has(deviceKey)) {
            return;
        }

        deviceKeys.add(deviceKey);
        deviceKeysByBranch.set(branchId, deviceKeys);
        branch.devices = [...(branch.devices ?? []), device];
    };

    rows.forEach((row) => {
        if (!isFlatDeviceRow(row)) {
            const existingBranch = groupedByBranch.get(row.id);
            const normalizedBranch = {
                ...existingBranch,
                ...row,
                devices: existingBranch?.devices ?? [],
            };

            (row.devices ?? []).forEach((device) => addDeviceToBranch(row.id, normalizedBranch, device));
            groupedByBranch.set(row.id, normalizedBranch);
            return;
        }

        const branch = row.branch;
        const branchId = branch?.id ?? row.branchId;
        const existingBranch = groupedByBranch.get(branchId);
        const normalizedBranch: DeviceCountBranchRow = existingBranch ?? {
            id: branchId,
            name: branch?.name,
            companyName: branch?.companyName,
            groupName: branch?.groupName,
            state: branch?.state,
            branchState: branch?.branchState,
            nextMaintainceDay: branch?.nextMaintainceDay,
            nextMaintenanceDay: branch?.nextMaintenanceDay,
            assignedDistributor: branch?.assignedDistributor,
            assignedDistributorName: branch?.assignedDistributorName,
            nextMaintenanceDate: branch?.nextMaintenanceDate,
            maintenanceDays: branch?.maintenanceDays,
            maintenanceState: branch?.maintenanceState,
            devices: [],
        };

        addDeviceToBranch(branchId, normalizedBranch, {
            id: row.device?.id ?? row.deviceId,
            historyId: row.id,
            deviceName: row.device?.deviceName,
            model: row.device?.model,
            batchNumber: row.device?.batchNumber,
            passportId: row.device?.passportId,
            active: row.active,
            changedAt: row.changedAt,
            pausedUntil: row.pausedUntil,
            startDate: row.startDate,
            endDate: row.endDate,
            fromDate: row.fromDate,
            toDate: row.toDate,
            pauseStartDate: row.pauseStartDate,
            pauseEndDate: row.pauseEndDate,
            inactiveStartDate: row.inactiveStartDate,
            inactiveEndDate: row.inactiveEndDate,
            changedByUserId: row.changedByUserId,
            operation: row.operation,
        });

        groupedByBranch.set(branchId, normalizedBranch);
    });

    return Array.from(groupedByBranch.values());
};
